import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CloseIcon } from '@/components/Icons/CloseIcon';
import { Minimize } from '@/components/Icons/Minimize';
import { Reset } from '@/components/Icons/Reset';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useSessionId } from '@/hooks/useSessionId';
import { useSocket } from '@/hooks/useSocket';
import { useWidgetState } from '@/hooks/useWidgetState';
import { closeSessionIfEmptySelector, integrationIdSelector, reverseHeaderButtons, showResetSelector, widgetPositionSelector, } from '@/state/config';
import { chatEventCountSelector, chatEventsState, chatModalState, isMobileSelector, sessionOverviewState, } from '@/state/session';
import styles from './ActionContainer.module.scss';
export const ActionContainer = () => {
    const { closeSession } = useSocket();
    const closeSessionIfEmpty = useRecoilValue(closeSessionIfEmptySelector);
    const integrationId = useRecoilValue(integrationIdSelector);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    const setChatModal = useSetRecoilState(chatModalState);
    const setChatEvents = useSetRecoilState(chatEventsState);
    const setSessionOverview = useSetRecoilState(sessionOverviewState);
    const setSessionId = useSessionId(integrationId);
    const { minimizeWidget } = useWidgetState();
    const isMobile = useRecoilValue(isMobileSelector);
    const chatCount = useRecoilValue(chatEventCountSelector);
    const showReset = useRecoilValue(showResetSelector);
    const shouldReverseButtons = useRecoilValue(reverseHeaderButtons);
    const { trackEvent } = useAnalytics();
    const handleReset = useCallback(() => {
        setChatEvents([]);
        setChatModal(null);
        setSessionId(null);
        setSessionOverview(null);
    }, [setChatEvents, setChatModal, setSessionId, setSessionOverview]);
    const handleKeyDownReset = useCallback((e) => {
        if (e.key === 'Enter') {
            handleReset();
        }
    }, [handleReset]);
    const handleMinimizeButton = useCallback(() => {
        trackEvent(AnalyticsEvent.MinimizeIconClicked);
        minimizeWidget();
    }, [minimizeWidget, trackEvent]);
    const handleKeyDownMinimize = useCallback((e) => {
        if (e.key === 'Enter') {
            handleMinimizeButton();
        }
    }, [handleMinimizeButton]);
    const handleCloseButton = useCallback(() => {
        trackEvent(AnalyticsEvent.CloseIconClicked);
        if (chatCount === 0) {
            if (closeSessionIfEmpty) {
                closeSession();
            }
            else {
                minimizeWidget();
            }
        }
        else {
            setChatModal("closing" /* ChatModal.Closing */);
        }
    }, [
        trackEvent,
        chatCount,
        closeSessionIfEmpty,
        closeSession,
        minimizeWidget,
        setChatModal,
    ]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            handleCloseButton();
        }
    }, [handleCloseButton]);
    return (_jsxs("div", { className: cn(styles.actionsContainer, {
            [styles.reversed]: shouldReverseButtons && isMobile,
        }), children: [showReset && (_jsx("span", { role: "button", tabIndex: 0, className: styles.headerButton, onClick: handleReset, onKeyDown: handleKeyDownReset, children: _jsx(Reset, {}) })), !showReset && widgetPosition !== 'embed' && (_jsx("span", { role: "button", tabIndex: 0, className: cn("minimize-btn" /* TestClassName.MinimizeButton */, styles.headerButton), onClick: handleMinimizeButton, onKeyDown: handleKeyDownMinimize, children: _jsx(Minimize, {}) })), !showReset && (_jsx("span", { role: "button", tabIndex: 0, className: cn("close-btn" /* TestClassName.CloseButton */, styles.headerButton), onClick: handleCloseButton, onKeyDown: handleKeyDown, children: _jsx(CloseIcon, {}) }))] }));
};
